/**
 * TrustBar - Horizontal trust signal bar below hero
 * 
 * CHANGELOG:
 * - 2026-03-05: Restructured to horizontal icon-left + two-line-text layout
 * - 2026-03-05: Increased icon size to w-10 h-10 (text-3xl) for readability
 * - 2026-03-05: Increased text to text-sm for desktop readability
 * - 2026-03-05: Split text into line1/line2 per trust signal
 * - Previous: Grid layout with single-line text, smaller icons
 */
/**
 * TrustBar Component
 * 
 * Project: SunMarbella Holiday Rental Website
 * Version: 1.0.0
 * Last Modified: 2025-03-09
 * 
 * Purpose:
 * Displays trust indicators, testimonials, and social proof to build credibility.
 * Shows customer reviews, ratings, and verification badges.
 * 
 * Features:
 * - Customer testimonials with ratings
 * - Star ratings display
 * - Customer names and locations
 * - Trust badges (verified, featured, etc.)
 * - Responsive carousel on mobile
 * - Static grid on desktop
 * 
 * Dependencies:
 * - React 19.2.4 (JSX)
 * - Font Awesome 6.5 (star icons)
 * - TailwindCSS 4.1 (responsive styling)
 * 
 * Changelog:
 * - 1.0.0 (2025-03-09): Initial implementation
 */

import React from 'react';
import { ProIcon } from './ProIcon';

type TrustUsp = {
  icon: string;
  line1: string;
  line2: string;
};

export const TrustBar = () => {
  const usps: TrustUsp[] = [
    { icon: 'building-magnifying-glass', line1: 'Inspected', line2: 'Homes' },
    { icon: 'bullseye-arrow', line1: 'Direct', line2: 'Rates' },
    { icon: 'location-check', line1: 'Real Local', line2: 'Team' },
    { icon: 'person-running-fast', line1: 'Fast', line2: 'Help' },
    { icon: 'shield-user', line1: 'Secure', line2: 'Booking' },
  ];

  return (
    <div className="w-full bg-slate-800 text-white py-8 relative overflow-hidden">
      {/* Social Proof Signal */}
      <div className="flex justify-center mb-3 px-4">
        <div className="bg-slate-700 text-white px-3 sm:px-6 lg:px-8 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-black uppercase tracking-[0.12em] sm:tracking-[0.16em] lg:tracking-[0.2em] shadow-2xl border border-slate-600 text-center leading-tight whitespace-normal sm:whitespace-nowrap">
          500+ Family Stays Hosted Across Marbella
        </div>
      </div>

      {/* Mobile & Tablet: text only */}
      <div className="lg:hidden max-w-7xl mx-auto px-4">
        <div className="text-center text-sm sm:text-sm font-extrabold uppercase tracking-wide text-white/90 leading-relaxed">
          Inspected homes • Direct rates • Real local support
        </div>
      </div>

      <div className="hidden lg:block max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center gap-6 lg:gap-10 xl:gap-14 flex-nowrap">
          {usps.map((usp, idx) => (
            <div key={idx} className="flex items-center gap-2.5 group">
              <div className="p-3 bg-white/10 rounded-2xl group-hover:bg-brand-ocean transition-all duration-300 shadow-sm grid place-items-center text-white">
                <ProIcon icon={usp.icon} family="sharp" className="text-xl" />
              </div>
              <div className="flex flex-col leading-none">
                <span className="text-sm font-black uppercase tracking-tight text-white/90">
                  {usp.line1}
                </span>
                <span className="text-sm font-black uppercase tracking-tight text-white/70">
                  {usp.line2}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
